// src/components/dashboard/Sidebar.jsx
import { useState, useEffect } from "react";
import {
  FiCamera,
  FiVideo,
  FiLogOut,
  FiSun,
  FiMoon,
  FiChevronDown,
  FiChevronRight,
  FiMenu,
  FiX,
  FiUsers,
  FiActivity,
  FiLayers,
} from "react-icons/fi";
import { useTheme } from "../../context/ThemeContext";

// ✅ Import stores
import { useAuthStore } from "../../stores/authStore";
import { useOrganizationStore } from "../../stores/organizationStore";
import { useUIStore } from "../../stores/uiStore";

export default function Sidebar({
  activeSection,
  setActiveSection,
  onSelectOrg,
  onSelectSite,
  onLogout,
}) {
  const { theme, toggleTheme } = useTheme();

  // ==========================================
  // GET STATE FROM STORES
  // ==========================================
  
  const currentUser = useAuthStore(s => s.currentUser);
  const activeOrgId = useAuthStore(s => s.activeOrgId);
  const activeSiteId = useAuthStore(s => s.activeSiteId);
  const organizations = useOrganizationStore(s => s.organizations);
  
  const isPlatformAdmin = currentUser?.global_role === 'platform_admin';
  
  const sites = isPlatformAdmin 
    ? []
    : organizations.flatMap(org => 
        (org.sites || []).map(site => ({ ...site, organization: org.id }))
      );

  // ==========================================
  // LOCAL UI STATE
  // ==========================================
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openOrgId, setOpenOrgId] = useState(null);
  const [openSiteId, setOpenSiteId] = useState(null);

  useEffect(() => {
    if (activeOrgId) setOpenOrgId(activeOrgId);
    if (activeSiteId) setOpenSiteId(activeSiteId);
  }, [activeOrgId, activeSiteId]);


// ==========================================
// THEME-AWARE STYLES (CONSISTENT PATTERN)
// ==========================================

const activeStyle =
  theme === "dark"
    ? "bg-neutral-750 text-white"
    : "bg-gray-200 text-gray-900";

const hoverStyle =
  theme === "dark"
    ? "hover:bg-neutral-700"
    : "hover:bg-gray-100";

const textStyle =
  theme === "dark"
    ? "text-slate-100"
    : "text-gray-900";

  const roleLabel = (role) => {
    if (role === "platform_admin") return "Platform Admin";
    if (role === "org_admin") return "Org Admin";
    return "User";
  };

  /* ===============================
     USER BLOCK
     =============================== */

  const UserBlock = () =>
    currentUser && (
      <div className="px-4 py-3 border-t text-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-blue-600 text-white flex items-center justify-center font-semibold">
            {(currentUser.name || currentUser.email || "?")[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">
              {currentUser.name || "User"}
            </div>
            <div className="text-xs opacity-70 truncate">
              {currentUser.email}
            </div>
            <div className="text-[10px] uppercase opacity-60">
              {roleLabel(currentUser.global_role)}
            </div>
          </div>
        </div>
      </div>
    );

  const Footer = () => (
    <div className="p-4 border-t flex items-center justify-between">
      <button
        onClick={toggleTheme}
        className={`p-2 rounded-full ${
          theme === "dark"
            ? "hover:bg-gray-700 text-yellow-400"
            : "hover:bg-gray-100 text-gray-600"
        }`}
      >
        {theme === "dark" ? <FiSun /> : <FiMoon />}
      </button>

      <button
        onClick={onLogout}
        className="flex items-center gap-2 text-sm text-red-500 hover:text-red-600"
      >
        <FiLogOut />
        Logout
      </button>
    </div>
  );

  /* ===============================
     SIDEBAR CONTENT
     =============================== */

  const SidebarContent = (
    <div className="flex h-full flex-col">
      {/* LOGO */}
      <div className="p-4 flex justify-center border-b">
        <img src="/images/viewmax_logo.png" alt="Viewmax" className="h-10" />
      </div>

      <nav className="flex-1 p-4 overflow-y-auto space-y-6">
        {/* ===== ORG → SITE TREE (PRIMARY NAV) ===== */}
        <div>
          <div className="text-xs uppercase opacity-60 mb-2 px-2">
            {isPlatformAdmin ? "Organizations" : "Sites"}
          </div>

          {(isPlatformAdmin ? organizations : sites).map((item) => {

if (isPlatformAdmin) {
  const org = item;
  const orgOpen = org.id === openOrgId;

  return (
    <div key={org.id}>
      {/* Organization Button */}
      <button
        onClick={() => {
          setOpenOrgId(orgOpen ? null : org.id);
          onSelectOrg?.(org.id);
        }}
        className={`flex justify-between items-center w-full px-3 py-2 rounded transition-colors ${textStyle} ${
          org.id === activeOrgId ? activeStyle : hoverStyle
        }`}
      >
        <span className="truncate">{org.name}</span>
        {orgOpen ? <FiChevronDown /> : <FiChevronRight />}
      </button>

      {/* Sites (when org is expanded) */}
      {orgOpen &&
        org.sites?.map((site) => {
          const siteOpen = openSiteId === site.id;
          
          return (
            <div key={site.id} className="ml-4">
              {/* Site Button (collapsible) */}
              <button
                onClick={() => {
                  setOpenSiteId(siteOpen ? null : site.id);
                  onSelectSite(org.id, site.id);
                }}
                className={`flex justify-between items-center w-full px-3 py-2 rounded transition-colors ${textStyle} ${
                  site.id === activeSiteId ? activeStyle : hoverStyle
                }`}
              >
                <span className="truncate">{site.name}</span>
                {siteOpen ? <FiChevronDown /> : <FiChevronRight />}
              </button>

              {/* Cameras & Events (when site is expanded) */}
              {siteOpen && (
                <div className="ml-4 space-y-1">
                  {/* Cameras */}
                  <button
                    onClick={() => {
                      onSelectSite(org.id, site.id);
                      setActiveSection("cameras");
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded transition-colors text-sm ${
                      activeSection === "cameras"
                        ? activeStyle
                        : `${textStyle} ${hoverStyle}`
                    }`}
                  >
                    <FiCamera className="w-4 h-4" />
                    Cameras
                  </button>

                  {/* Events */}
                  <button
                    onClick={() => {
                      onSelectSite(org.id, site.id);
                      setActiveSection("events");
                    }}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded transition-colors text-sm ${
                      activeSection === "events"
                        ? activeStyle
                        : `${textStyle} ${hoverStyle}`
                    }`}
                  >
                    <FiVideo className="w-4 h-4" />
                    Events
                  </button>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
}
            // ORG ADMIN — SITE ONLY
            const site = item;
            const siteOpen = site.id === openSiteId;

            return (
              <div key={site.id}>
                <button
                  onClick={() => setOpenSiteId(siteOpen ? null : site.id)}
                  className={`flex justify-between items-center w-full px-3 py-2 rounded transition-colors ${textStyle} ${
                    site.id === activeSiteId
                      ? activeStyle
                      : hoverStyle
                  }`}
                >
                  <span className="truncate">{site.name}</span>
                  {siteOpen ? <FiChevronDown /> : <FiChevronRight />}
                </button>

                {siteOpen && (
                  <div className="ml-4">
                    <button
                      onClick={() => {
                        onSelectSite(site.organization, site.id);
                        setActiveSection("cameras");
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2 rounded transition-colors ${textStyle} ${hoverStyle}`}
                    >
                      <FiCamera /> Cameras
                    </button>

                    <button
                      onClick={() => {
                        onSelectSite(site.organization, site.id);
                        setActiveSection("events");
                      }}
                      className={`flex items-center gap-2 w-full px-3 py-2 rounded transition-colors ${textStyle} ${hoverStyle}`}
                    >
                      <FiVideo /> Events
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>



{/* ===== MANAGEMENT (ORG ADMIN ONLY) ===== */}
{!isPlatformAdmin && currentUser?.global_role === 'org_admin' && (
  <div className="pt-4 border-t">
    <div className="text-xs uppercase opacity-60 mb-2 px-2">
      Management
    </div>

    <button
      onClick={() => setActiveSection("sites")}
      className={`flex items-center gap-3 w-full px-3 py-2 rounded transition-colors ${
        activeSection === "sites"
          ? activeStyle
          : `${textStyle} ${hoverStyle}`
      }`}
    >
      <FiLayers />
      Sites
    </button>

    <button
      onClick={() => setActiveSection("users")}
      className={`flex items-center gap-3 w-full px-3 py-2 rounded transition-colors ${
        activeSection === "users"
          ? activeStyle
          : `${textStyle} ${hoverStyle}`
      }`}
    >
      <FiUsers />
      Users
    </button>
  </div>
)}





        {/* ===== SYSTEM (SECONDARY, PLATFORM ADMIN ONLY) ===== */}
        {isPlatformAdmin && (
          <div className="pt-4 border-t">
            <div className="text-xs uppercase opacity-60 mb-2 px-2">
              System
            </div>

            <button
              onClick={() => {
                setActiveSection("organizations");
                onSelectOrg?.(null);
              }}
              className={`flex items-center gap-3 w-full px-3 py-2 rounded transition-colors ${
                activeSection === "organizations"
                  ? activeStyle
                  : `${textStyle} ${hoverStyle}`
              }`}
            >
              <FiLayers />
              Organizations
            </button>

            <button
              onClick={() => setActiveSection("users")}
              className={`flex items-center gap-3 w-full px-3 py-2 rounded transition-colors ${
                activeSection === "users"
                  ? activeStyle
                  : `${textStyle} ${hoverStyle}`
              }`}
            >
              <FiUsers />
              Users
            </button>

    {/* ✅ System Health */}
    <button
      onClick={() => setActiveSection("system_health")}
      className={`flex items-center gap-3 w-full px-3 py-2 rounded transition-colors ${
        activeSection === "system_health"
          ? activeStyle
          : `${textStyle} ${hoverStyle}`
      }`}
    >
      <FiActivity />
      System Health
    </button>


          </div>
        )}
      </nav>

      <UserBlock />
      <Footer />
    </div>
  );

  /* ===============================
     MOBILE + DESKTOP
     =============================== */
  return (
    <>
      {/* Mobile Top Bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 border-b bg-white dark:bg-neutral-800">
        <button onClick={() => setMobileOpen(true)}>
          <FiMenu />
        </button>
        <span className="text-sm font-semibold">Viewmax</span>
        <div className="w-6" />
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed top-0 left-0 h-full w-64 border-r bg-white dark:bg-neutral-800">
        {SidebarContent}
      </aside>

      {/* Mobile Drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="relative w-64 h-full bg-white dark:bg-neutral-800">
            <div className="absolute top-3 right-3">
              <button onClick={() => setMobileOpen(false)}>
                <FiX />
              </button>
            </div>
            {SidebarContent}
          </aside>
        </div>
      )}
    </>
  );

}

